const WORD_INITIAL_PATTERN = /\b\w/g

const GROUP_LABELS = {
  config: 'Configuration',
  filter_logic: 'Filters',
  metadata: 'Metadata',
  schedule_config: 'Schedule',
  sort_logic: 'Sorting',
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function flattenPayload(value, prefix = '', result = new Map()) {
  if (!isPlainObject(value) || Object.keys(value).length === 0) {
    if (prefix) result.set(prefix, value)
    return result
  }

  Object.entries(value).forEach(([key, nestedValue]) => {
    const path = prefix ? `${prefix}.${key}` : key
    if (isPlainObject(nestedValue) && Object.keys(nestedValue).length > 0) {
      flattenPayload(nestedValue, path, result)
      return
    }
    result.set(path, nestedValue)
  })

  return result
}

function humanizeKey(value) {
  return String(value)
    .replaceAll('_', ' ')
    .replace(WORD_INITIAL_PATTERN, (character) => character.toUpperCase())
}

function labelForPath(path) {
  return path
    .split('.')
    .map((part) => GROUP_LABELS[part] || humanizeKey(part))
    .join(' · ')
}

function stableValue(value) {
  if (Array.isArray(value)) return JSON.stringify(value)
  if (isPlainObject(value)) {
    return JSON.stringify(Object.fromEntries(Object.entries(value).sort(([first], [second]) => first.localeCompare(second))))
  }
  return JSON.stringify(value)
}

export function formatReviewValue(value, { missing = false } = {}) {
  if (missing) return 'Not configured'
  if (value === null || value === undefined || value === '') return 'None'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (!value.length) return 'None'
    return value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))
      ? value.join(', ')
      : JSON.stringify(value)
  }
  if (isPlainObject(value)) return Object.keys(value).length ? JSON.stringify(value) : 'None'
  return String(value)
}

export function buildPayloadChangeItems({
  current = null,
  proposed = {},
  fieldMetadata = {},
  ignorePaths = [],
  defaultImpact = 'Changes the submitted runtime configuration',
} = {}) {
  const currentFields = flattenPayload(current || {})
  const proposedFields = flattenPayload(proposed || {})
  const paths = [...new Set([...proposedFields.keys(), ...currentFields.keys()])]
  const ignored = new Set(ignorePaths)

  return paths.flatMap((path) => {
    if (ignored.has(path)) return []

    const hasCurrent = currentFields.has(path)
    const hasProposed = proposedFields.has(path)
    const currentValue = currentFields.get(path)
    const proposedValue = proposedFields.get(path)
    if (hasCurrent === hasProposed && stableValue(currentValue) === stableValue(proposedValue)) return []

    const metadata = fieldMetadata[path] || {}
    const format = metadata.format || formatReviewValue
    const formatContext = { currentValue, proposedValue, path }
    return [{
      key: path,
      label: metadata.label || labelForPath(path),
      current: format(currentValue, { ...formatContext, missing: !hasCurrent, side: 'current' }),
      proposed: format(proposedValue, { ...formatContext, missing: !hasProposed, side: 'proposed' }),
      impact: metadata.impact || defaultImpact,
    }]
  })
}
